import { FileStack } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { CollapsibleSection } from '@/features/reimbursements/components/detail/CollapsibleSection'
import tableStyles from '@/features/reimbursements/styles/listingTables.module.css'
import { FILE_TYPES } from '../../constants/residentConstants'
import type { DocumentHistoryRow, DocumentHistoryTableProps } from '../../types'

/** Same listing chrome as the Reimbursement detail history tables (design parity). */
const historyListingTableClass = cn(
  tableStyles.listingTable,
  tableStyles.companyListingTable,
  'w-full table-fixed text-sm',
  '[&_thead_th]:!whitespace-nowrap [&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
  '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
  '[&_tbody_td]:!align-middle [&_tbody_td]:break-words',
)

/** Matches company Payments tab file links for consistency with Uploaded documents. */
const DOC_FILE_LINK =
  'text-sm font-medium text-[rgb(21_151_255)] underline-offset-2 transition hover:text-[#0d7acc] hover:underline'

/**
 * Angular Document History pipe: `date: 'MM/dd/yyyy hh:mm a'`.
 * Handles ISO strings and unix epoch seconds/ms (common API shapes).
 */
function formatUploadedDate(value?: string | number | null): string {
  if (!value && value !== 0) return ''
  const raw = String(value).trim()
  let date: Date
  if (typeof value === 'number') {
    date = new Date(value < 1e12 ? value * 1000 : value)
  } else if (/^\d{10,13}$/.test(raw)) {
    const numeric = Number(raw)
    date = new Date(raw.length >= 13 ? numeric : numeric * 1000)
  } else {
    date = new Date(
      raw.includes(' ') && !raw.includes('T') ? raw.replace(' ', 'T') : raw,
    )
  }
  if (Number.isNaN(date.getTime())) return raw
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  if (hours === 0) hours = 12
  const hh = String(hours).padStart(2, '0')
  return `${mm}/${dd}/${yyyy} ${hh}:${minutes} ${ampm}`
}

function resolveFilePath(row: DocumentHistoryRow): string {
  return String(
    row.file_path ?? row.filepath ?? row.attachment_path ?? row.url ?? row.path ?? '',
  ).trim()
}

/**
 * Mirrors Angular's "Document History" table at the bottom of the
 * Reimbursement tab. Collapsible drawer with ~5 visible rows + vertical scroll.
 */
export function DocumentHistoryTable({
  rows,
  onDownload,
  processing = false,
}: DocumentHistoryTableProps) {
  const list = rows ?? []
  return (
    <CollapsibleSection
      title="Document History"
      icon={<FileStack className="h-4 w-4" />}
      recordCount={list.length}
      testId="resident-document-history"
    >
      {list.length ? (
      <>
      <div className="hidden overflow-hidden rounded-lg border border-slate-200 md:block" data-testid="resident-document-history-table-card">
        <div className={cn(tableStyles.tableResponsiveScroll, tableStyles.fiveRowTableScroll, 'touch-scroll')}>
          <table className={historyListingTableClass} data-testid="resident-document-history-table">
            <thead>
              <tr>
                <th>Uploaded</th>
                <th>File Type</th>
                <th>Filename</th>
                <th>Uploaded By</th>
                <th>Action</th>
                <th>Action By</th>
                <th>Action Date</th>
              </tr>
            </thead>
            <tbody>
          {list.map((row, index) => {
            const rowId = String(row.attachment_id || index)
            const filePath = resolveFilePath(row)
            const canOpen = Boolean(onDownload && row.attachment_id && filePath)
            return (
              <tr
                key={rowId}
                data-testid={`resident-document-history-row-${rowId}`}
                className="hover:bg-slate-50"
              >
                <td>{formatUploadedDate(row.created_at) || '-'}</td>
                <td>
                  {row.is_final_ledger === '1' ? 'Final ' : ''}
                  {FILE_TYPES[String(row.type || '').toUpperCase()] || row.type || ''}
                </td>
                <td>
                  {canOpen ? (
                    <button
                      type="button"
                      disabled={processing}
                      onClick={() =>
                        onDownload?.({
                          ...row,
                          file_path: filePath,
                          filepath: filePath,
                        })
                      }
                      data-testid={`resident-document-history-row-${rowId}-download`}
                      className={cn(DOC_FILE_LINK, 'disabled:cursor-not-allowed disabled:opacity-60')}
                    >
                      {row.filename || '-'}
                    </button>
                  ) : (
                    <span className="text-sm text-slate-800">{row.filename || '-'}</span>
                  )}
                </td>
                <td>{row.uploaded_by || '-'}</td>
                <td>{row.action || '-'}</td>
                <td>{row.action_by || '-'}</td>
                <td>{row.action_date || '-'}</td>
              </tr>
            )
          })}
            </tbody>
          </table>
        </div>
      </div>
      <ul
        className="max-h-[min(50vh,22rem)] space-y-3 overflow-y-auto overscroll-contain p-3 md:hidden"
        data-testid="resident-document-history-list-mobile"
      >
        {list.map((row, index) => {
          const rowId = String(row.attachment_id || index)
          const filePath = resolveFilePath(row)
          const canOpen = Boolean(onDownload && row.attachment_id && filePath)
          return (
            <li
              key={rowId}
              className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm"
              data-testid={`resident-document-history-card-${rowId}`}
            >
              <CardField label="Uploaded" value={formatUploadedDate(row.created_at) || '-'} />
              <CardField
                label="File Type"
                value={
                  <>
                    {row.is_final_ledger === '1' ? 'Final ' : ''}
                    {FILE_TYPES[String(row.type || '').toUpperCase()] || row.type || ''}
                  </>
                }
              />
              <CardField
                label="Filename"
                value={
                  canOpen ? (
                    <button
                      type="button"
                      disabled={processing}
                      onClick={() =>
                        onDownload?.({
                          ...row,
                          file_path: filePath,
                          filepath: filePath,
                        })
                      }
                      data-testid={`resident-document-history-card-${rowId}-download`}
                      className={cn(
                        DOC_FILE_LINK,
                        'break-all text-left disabled:cursor-not-allowed disabled:opacity-60',
                      )}
                    >
                      {row.filename || '-'}
                    </button>
                  ) : (
                    <span>{row.filename || '-'}</span>
                  )
                }
              />
              <CardField label="Uploaded By" value={row.uploaded_by || '-'} />
              <CardField label="Action" value={row.action || '-'} />
              <CardField label="Action By" value={row.action_by || '-'} />
              <CardField label="Action Date" value={row.action_date || '-'} />
            </li>
          )
        })}
      </ul>
      </>
      ) : (
        <div className="rounded-md bg-sky-50 px-4 py-3 text-sm font-medium text-slate-500">
          No Data Available
        </div>
      )}
    </CollapsibleSection>
  )
}

function CardField({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <p className="mt-2 break-words text-slate-800 first:mt-0">
      <strong className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </strong>
      {value ?? '-'}
    </p>
  )
}
